import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { ivyAffiliateApis } from "@services/apis/IvyAffiliate";
import { AssignIvyMerchantIntroducerRequest } from "@services/models/IvyAffiliate/AssignIvyMerchantIntroducerModel";
import React, { useEffect, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface AssignIvyIntroducerSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (merchantId: number) => void;
  initialMerchantId?: string;
}

const AssignIvyIntroducerSlider: React.FC<AssignIvyIntroducerSliderProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialMerchantId = "",
}) => {
  const [merchantId, setMerchantId] = useState(initialMerchantId);
  const [introducerUserId, setIntroducerUserId] = useState("");
  const [payinRefPct, setPayinRefPct] = useState("");
  const [payoutRefPct, setPayoutRefPct] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { handleError, handleSuccess } = useSnackbarMessage();

  useEffect(() => {
    if (isOpen) {
      setMerchantId(initialMerchantId);
    }
  }, [isOpen, initialMerchantId]);

  const resetForm = () => {
    setMerchantId(initialMerchantId);
    setIntroducerUserId("");
    setPayinRefPct("");
    setPayoutRefPct("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isValid = () => {
    const merchantIdNum = parseInt(merchantId.trim(), 10);
    const introducerUserIdNum = parseInt(introducerUserId.trim(), 10);
    return (
      merchantId.trim().length > 0 &&
      !isNaN(merchantIdNum) &&
      merchantIdNum > 0 &&
      introducerUserId.trim().length > 0 &&
      !isNaN(introducerUserIdNum) &&
      introducerUserIdNum > 0
    );
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      handleError({
        message: "Merchant ID and Introducer User ID are required",
      });
      return;
    }

    const payload: AssignIvyMerchantIntroducerRequest = {
      merchantId: parseInt(merchantId.trim(), 10),
      introducerUserId: parseInt(introducerUserId.trim(), 10),
    };

    if (payinRefPct.trim() !== "") {
      const pct = parseFloat(payinRefPct.trim());
      if (isNaN(pct)) {
        handleError({ message: "Pay-in referral % must be a valid number" });
        return;
      }
      payload.payinRefPct = pct;
    }

    if (payoutRefPct.trim() !== "") {
      const pct = parseFloat(payoutRefPct.trim());
      if (isNaN(pct)) {
        handleError({ message: "Pay-out referral % must be a valid number" });
        return;
      }
      payload.payoutRefPct = pct;
    }

    try {
      setIsLoading(true);
      const response =
        await ivyAffiliateApis.assignIvyMerchantIntroducer(payload);
      handleSuccess({
        message:
          response.msg || "Ivy merchant introducer assigned successfully",
      });
      onSuccess?.(payload.merchantId);
      resetForm();
      onClose();
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ||
          "Failed to assign Ivy merchant introducer. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="Assign Ivy Merchant Introducer"
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
    >
      <div className="flex flex-col px-[16px] py-[16px] gap-4">
        <GlobalTextInputField
          title="Merchant ID"
          placeholder="Enter merchant ID"
          value={merchantId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setMerchantId(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Introducer User ID"
          placeholder="Enter affiliate / introducer user ID"
          value={introducerUserId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setIntroducerUserId(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Pay-in Referral % (Optional)"
          placeholder="e.g. 1.5"
          value={payinRefPct}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPayinRefPct(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Pay-out Referral % (Optional)"
          placeholder="e.g. 1.5"
          value={payoutRefPct}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPayoutRefPct(e.target.value)
          }
        />
        <CustomButton
          text="ASSIGN INTRODUCER"
          onClick={handleSubmit}
          isActive={isValid()}
          isButtonLoading={isLoading}
        />
      </div>
    </SlidingPanel>
  );
};

export default AssignIvyIntroducerSlider;
